// The in-browser fairness verifier — never trust, always re-derive.
//
// Seven independent checks per race, every input public:
//   1. racecard content hash (canonical JSON sha256)
//   2. seed hash — sha256(revealed seed) == the card's committed seedHash
//   3. chain link — sha256^chainIndex(seed) walks to the epoch's published head
//   4. composition — field/weights/silks/stage/weather/distance replay from
//      the commitment hash + published roster snapshot (the composition
//      attack closure: the world had no discretion)
//   5. order — Plackett-Luce re-derivation from (seed, commitmentHash, index)
//   6. physics — the v2 envelope (incl. quarter section times) re-derived
//   7. signature — RS256 over the content hash against the public JWKS

import { StreamGenerator, sampleOrder, sha256Hex } from '../static/js/fairness.js';
import { deriveTelemetryV2 } from '../static/js/telemetry-v2.js';
import { composeSlot } from './composition.js';
import { api } from './api.js';

function canonicalJson(value) {
  if (Array.isArray(value)) return `[${value.map(canonicalJson).join(',')}]`;
  if (value && typeof value === 'object') {
    const keys = Object.keys(value).sort();
    return `{${keys.map((k) => `${JSON.stringify(k)}:${canonicalJson(value[k])}`).join(',')}}`;
  }
  return JSON.stringify(value);
}

async function verifySignature(signatureJws, contentHash) {
  try {
    const [headerB64, payloadB64, sigB64] = signatureJws.split('.');
    const header = JSON.parse(atob(headerB64.replace(/-/g, '+').replace(/_/g, '/')));
    const payload = JSON.parse(atob(payloadB64.replace(/-/g, '+').replace(/_/g, '/')));
    if (payload.rcHash !== contentHash) return false;
    const jwks = await (await fetch('/.well-known/jwks.json')).json();
    const jwk = jwks.keys.find((k) => k.kid === header.kid);
    if (!jwk) return false;
    const key = await crypto.subtle.importKey(
      'jwk', jwk, { name: 'RSASSA-PKCS1-v1_5', hash: 'SHA-256' }, false, ['verify'],
    );
    const data = new TextEncoder().encode(`${headerB64}.${payloadB64}`);
    const signature = Uint8Array.from(
      atob(sigB64.replace(/-/g, '+').replace(/_/g, '/')), (c) => c.charCodeAt(0),
    );
    return crypto.subtle.verify('RSASSA-PKCS1-v1_5', key, signature, data);
  } catch {
    return false;
  }
}

/** Full verification. Returns [{label, ok|null, detail}] — ok null = pending. */
export async function verifyRaceById(raceId) {
  const { racecard: card, racecardHash, signature } = await api.racecard(raceId);
  const proof = await api.proof(raceId);
  const checks = [];

  const rebuiltHash = await sha256Hex(canonicalJson(card));
  checks.push({
    label: 'Racecard content hash',
    ok: rebuiltHash === racecardHash,
    detail: `${rebuiltHash.slice(0, 16)}…`,
  });

  checks.push({
    label: 'Publisher signature (RS256 / JWKS)',
    ok: await verifySignature(signature, racecardHash),
    detail: 'signed over the content hash',
  });

  // Composition replay from the public commitment + roster snapshot.
  const commitment = await api.epochCommitment(card.epoch);
  const roster = await api.epochRoster(card.epoch);
  const stageIds = commitment.commitment.stageRegistry
    ? (await composeStageIds(commitment)).stages : [];
  const weatherIds = (await composeStageIds(commitment)).weathers;
  const comp = await composeSlot(
    commitment.commitmentHash,
    card.chainIndex - 1,
    roster.roster,
    {
      distanceMix: commitment.commitment.distanceMix,
      stageIds,
      weatherIds,
      fieldSize: card.fieldSize,
    },
  );
  const cardRunners = card.runners.map((r) => [r.horseId, r.gate, r.weight, r.silk].join('|'));
  const compRunners = comp.runners.map((r) => [r.horseId, r.gate, r.weight, r.silk].join('|'));
  checks.push({
    label: 'Committed composition (field, weights, silks)',
    ok: JSON.stringify(cardRunners) === JSON.stringify(compRunners)
      && comp.distanceM === card.distanceM
      && comp.stageId === card.stage
      && comp.weatherId === card.weather,
    detail: 'replayed from the epoch commitment + roster snapshot',
  });

  if (!proof.revealed) {
    checks.push({ label: 'Seed reveal', ok: null, detail: 'pending — reveals when the result is official' });
    return { card, proof, checks };
  }

  checks.push({
    label: 'Seed hash commitment',
    ok: (await sha256Hex(proof.seed)) === card.seedHash,
    detail: card.seedHash.slice(0, 16) + '…',
  });

  let link = proof.seed;
  for (let i = 0; i < proof.chainIndex; i++) link = await sha256Hex(link);
  checks.push({
    label: `Chain link walks to the epoch head (${proof.chainIndex} steps)`,
    ok: link === proof.chainHead,
    detail: proof.chainHead.slice(0, 16) + '…',
  });

  const weights = card.runners.map((r) => r.weight);
  const gen = new StreamGenerator(proof.seed, proof.commitmentHash, String(proof.chainIndex));
  const order = await sampleOrder(gen, weights);
  checks.push({
    label: 'Finish order re-derivation',
    ok: JSON.stringify(order) === JSON.stringify(proof.order),
    detail: order.slice(0, 3).map((g) => `#${g + 1}`).join(' → '),
  });

  try {
    const playback = await api.playback(raceId);
    const envelope = await deriveTelemetryV2(
      proof.seed,
      proof.commitmentHash,
      String(proof.chainIndex),
      order,
      card.raceDurationMs,
      card.distanceM,
      card.runners.map((r) => r.archetype ?? null),
    );
    let physicsOk = envelope.horses.length === playback.physics.horses.length;
    for (let i = 0; i < envelope.horses.length && physicsOk; i++) {
      const a = envelope.horses[i];
      const b = playback.physics.horses[i];
      physicsOk = a.seed === b.seed
        && Math.abs(a.finishTime - b.finishTime) < 1e-9
        && JSON.stringify(a.sectionTimesMs) === JSON.stringify(b.sectionTimesMs);
    }
    checks.push({
      label: 'Physics envelope + quarter section times (v2)',
      ok: physicsOk,
      detail: 'telemetry re-derived under horseracing-telemetry:v2',
    });
  } catch {
    checks.push({ label: 'Physics envelope', ok: null, detail: 'playback not yet available' });
  }

  return { card, proof, checks };
}

async function composeStageIds(commitment) {
  // Stage/weather id lists ride the registry; v1 is the launch registry.
  // The registry payload is versioned + hash-committed; ids are stable per
  // version, so the launch lists are embedded here and future versions load
  // from the registry endpoint when it ships with growth-ops.
  return {
    stages: ['canyon', 'rose', 'desert', 'midnight'],
    weathers: ['clear', 'rain', 'mist', 'dust'],
  };
}
